import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { reportCrash } from '@/lib/crashReport';

interface Props {
  /** Имя экрана — попадает в отчёт о сбое (`saleus/<имя>`). */
  section: string;
  children: ReactNode;
}

/**
 * Защита экрана: падение одного экрана не должно ронять приложение целиком
 * и тем более липнуть к соседним (урок «сбой раздела ЛИПНУЛ к следующим»,
 * 07.10.2026). Поэтому у каждого экрана своя граница, и сбрасывается она при
 * повторе. Сбой уходит в общий учёт.
 */
export class ErrorBoundary extends Component<Props, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportCrash({
      kind: 'section',
      error,
      componentStack: info.componentStack,
      section: `saleus/${this.props.section}`,
    });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
        <TriangleAlert className="size-8 text-warn" aria-hidden />
        <p className="text-lg font-semibold">Экран не открылся</p>
        <p className="text-sm text-muted max-w-xs">Мы уже получили отчёт об ошибке. Попробуйте ещё раз.</p>
        <button
          type="button"
          onClick={() => this.setState({ failed: false })}
          className="min-h-11 px-5 rounded-xl bg-accent text-accent-ink font-semibold"
        >
          Повторить
        </button>
      </div>
    );
  }
}
